import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Search, ShoppingCart, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProductViewPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/commerce/products/${id}`, { withCredentials: true });
      setProduct(res.data);
    } catch (error) {
      console.error('[Product] Error:', error);
      toast.error('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#050814', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F5F7FF' }}>
        Loading product...
      </div>
    );
  }

  if (!product) {
    return (
      <div style={{ minHeight: '100vh', background: '#050814', padding: '2rem', color: '#F5F7FF' }}>
        Product not found
      </div>
    );
  }

  const funnelSteps = [
    { label: 'Searches', value: product.funnel.searches, icon: Search },
    { label: 'Clicks', value: product.funnel.clicks, icon: ArrowLeft },
    { label: 'Add to Cart', value: product.funnel.addToCart, icon: ShoppingCart },
    { label: 'Purchases', value: product.funnel.purchases, icon: CreditCard }
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#050814', color: '#F5F7FF', padding: '2rem' }}>
      {/* Back button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/commerce')}
        className="mb-6 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
      >
        <ArrowLeft size={16} className="mr-2" />
        Back to Dashboard
      </Button>

      {/* Product Header */}
      <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', marginBottom: '1.5rem' }}>
        <CardContent className="p-6">
          <div className="mb-3">
            <div style={{ fontSize: '0.75rem', color: 'hsl(var(--primary))', marginBottom: '0.5rem' }}>
              {product.category}
            </div>
            <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '1rem' }}>
              {product.name}
            </h1>
          </div>
          
          <div className="flex gap-6 items-center">
            <div>
              <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: 4 }}>
                Composite Score
              </div>
              <div style={{ fontSize: '2.625rem', fontWeight: 700, fontFamily: 'monospace', color: 'hsl(var(--success))' }}>
                {Math.round(product.compositeScore * 100)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: 4 }}>
                Velocity
              </div>
              <div style={{ fontSize: '2.625rem', fontWeight: 700, fontFamily: 'monospace', color: 'hsl(var(--success))' }}>
                +{product.velocity.toFixed(1)}%
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Conversion Funnel */}
      <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', marginBottom: '1.5rem' }}>
        <CardContent className="p-6">
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1.5rem' }}>
            Conversion Funnel (Last 7 Days)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {funnelSteps.map((step, idx) => {
              const Icon = step.icon;
              const conversionRate = idx > 0 ? (step.value / funnelSteps[idx - 1].value * 100) : 100;
              
              return (
                <div key={step.label} style={{ textAlign: 'center' }}>
                  <div style={{ 
                    background: 'hsl(var(--muted))',
                    padding: '1.5rem',
                    borderRadius: '0.5rem',
                    border: '1px solid hsl(var(--border))'
                  }}>
                    <Icon size={20} style={{ color: 'hsl(var(--primary))', margin: '0 auto 8px' }} />
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, fontFamily: 'monospace', color: 'hsl(var(--foreground))', marginBottom: 4 }}>
                      {step.value.toLocaleString()}
                    </div>
                    <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))', marginBottom: 4 }}>
                      {step.label}
                    </div>
                    {idx > 0 && (
                      <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--success))', fontFamily: 'monospace' }}>
                        {conversionRate.toFixed(1)}%
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Additional Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Price Trend */}
        <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}>
          <CardContent className="p-6">
            <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1.5rem' }}>
              Price Trend
            </h3>
            <div className="flex gap-4 items-end">
              {product.priceTrend.map((price, idx) => (
                <div key={idx} style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, fontFamily: 'monospace', color: 'hsl(var(--foreground))' }}>
                    ${price.toFixed(2)}
                  </div>
                  <div style={{ fontSize: '0.625rem', color: 'hsl(var(--muted-foreground))' }}>
                    {idx === 0 ? '30d ago' : idx === 1 ? '15d ago' : 'Today'}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Creator Influence */}
        <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}>
          <CardContent className="p-6">
            <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1.5rem' }}>
              Top Creators
            </h3>
            <div className="space-y-3">
              {product.topCreators.map((creator, idx) => (
                <div key={idx} style={{ 
                  padding: '0.75rem',
                  background: 'hsl(var(--muted))',
                  borderRadius: '0.25rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ fontSize: '0.875rem', color: 'hsl(var(--primary))' }}>
                    {creator}
                  </span>
                  <span style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))', fontFamily: 'monospace' }}>
                    #{idx + 1}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Forecast */}
        <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', gridColumn: 'span 2' }}>
          <CardContent className="p-6">
            <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1.5rem' }}>
              Forecast
            </h3>
            <div className="grid grid-cols-2 gap-6">
              <div style={{ textAlign: 'center', padding: '1.5rem', background: 'hsl(var(--muted))', borderRadius: '0.5rem' }}>
                <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: 8 }}>
                  7-Day Projection
                </div>
                <div style={{ fontSize: '2rem', fontWeight: 700, fontFamily: 'monospace', color: 'hsl(var(--foreground))' }}>
                  {product.forecast.day7.toLocaleString()}
                </div>
                <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>
                  units
                </div>
              </div>
              <div style={{ textAlign: 'center', padding: '1.5rem', background: 'hsl(var(--muted))', borderRadius: '0.5rem' }}>
                <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: 8 }}>
                  30-Day Projection
                </div>
                <div style={{ fontSize: '2rem', fontWeight: 700, fontFamily: 'monospace', color: 'hsl(var(--foreground))' }}>
                  {product.forecast.day30.toLocaleString()}
                </div>
                <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>
                  units
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
